/**
 * Chat Export — POST `/chat/{conversation_id}/export` (May 2026).
 *
 * Returns the conversation as a Markdown blob with a `Content-Disposition:
 * attachment` header. Powers the FE slash-command `/export` (typed in the
 * composer): the host hook saves the blob via a synthetic `<a download>`.
 *
 * The BE endpoint is JWT-protected (registered on the `protected_router`)
 * and renders the markdown server-side via
 * `src/api/v1/endpoints/chat_export.py` (DRY: reuses the same
 * `conversation_service.get_conversation` path the history endpoint uses).
 *
 * Why raw `fetch` and not `apiClient.post`:
 *   The default `apiClient` parses the response body as JSON (it falls
 *   back to text for non-JSON 2xx but it does NOT preserve the Blob).
 *   For a binary/text-stream download we need the raw Response so we can
 *   call `.blob()`. We still ride on the cookie auth (`credentials:
 *   "include"`) and the `Authorization` Bearer header when the desktop
 *   shell has registered a provider.
 */

import { apiClient } from "../ApiClient.js";
import { ApiError, AuthError, NetworkError } from "../errors.js";

/**
 * Fetch the Markdown export of a conversation as a Blob.
 *
 * Throws on non-2xx — mirrors the structured errors raised by `ApiClient`
 * so the caller can react with the usual `ApiError`/`AuthError` branches.
 */
export async function exportConversation(conversationId: string): Promise<Blob> {
  const base = apiClient.getBaseUrl();
  const url = `${base}/chat/${encodeURIComponent(conversationId)}/export`;

  // Mirror `ApiClient.request` headers: Bearer if a token provider is
  // registered (desktop), nothing on web (cookie auth handles it).
  const headers = new Headers();
  const token = await apiClient.getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers,
    });
  } catch (err) {
    throw new NetworkError(err instanceof Error ? err.message : "Network error", err);
  }

  if (response.ok) {
    return await response.blob();
  }

  // Parse a structured error body if the BE sent JSON; otherwise raw text.
  let detail: string | undefined;
  let parsedBody: unknown = null;
  try {
    const text = await response.text();
    try {
      parsedBody = JSON.parse(text);
      if (
        parsedBody &&
        typeof parsedBody === "object" &&
        "detail" in parsedBody &&
        typeof (parsedBody as { detail?: unknown }).detail === "string"
      ) {
        detail = (parsedBody as { detail: string }).detail;
      }
    } catch {
      detail = text || undefined;
    }
  } catch {
    // body unreadable
  }

  if (response.status === 401) {
    throw new AuthError(detail ?? "Unauthorized", response, parsedBody);
  }

  throw new ApiError(
    detail ?? `HTTP ${response.status}: ${response.statusText}`,
    response.status,
    response,
    parsedBody,
  );
}
